import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { errText } from "../../auth/EmailCode";
import ProfileHeader from "./ProfileHeader";
import { AboutSection, SkillsSection } from "./ProfileFields";
import ProfileList from "./ProfileList";
import ProfileCv from "./ProfileCv";
import StatusTips from "./StatusTips";
import ProfileSettings from "./ProfileSettings";
import InviteFriends from "./InviteFriends";
import KeshTip from "../../studio/KeshTip";
import { keshContext } from "../../studio/constants";
import { PROFILE_HELP, SECTION_LABEL } from "./profileHelp";

const KEYS = ["name", "headline", "phone", "location", "linkedin", "work_status", "target_roles", "summary", "experience", "education", "skills", "certifications"];
const pick = (p) => Object.fromEntries(KEYS.map((k) => [k, p[k] ?? (k === "experience" || k === "education" ? [] : "")]));
const CHECKS = [(p) => p.name, (p) => p.headline, (p) => p.location, (p) => p.work_status, (p) => p.summary, (p) => p.experience?.length, (p) => p.education?.length, (p) => p.skills];

export default function Profile({ baseCv, onCvChange, onGo }) {
  const { user, checkAuth } = useAuth();
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState("");
  const [status, setStatus] = useState("saved");
  const [version, setVersion] = useState(0);
  const [isNew] = useState(() => Boolean(new URLSearchParams(window.location.search).get("new")));
  const [focus, setFocus] = useState("");

  useEffect(() => {
    const key = focus || "start";
    keshContext({ inline: true, page: `My Profile, ${SECTION_LABEL[key]} section`, suggestions: PROFILE_HELP[key].ask });
  }, [focus]);
  useEffect(() => () => keshContext(null), []);

  useEffect(() => {
    api.get("/profile").then(({ data }) => { setP(data); setSaved(JSON.stringify(pick(data))); })
      .catch(() => toast.error("We couldn't load your profile. Please refresh the page."));
  }, []);

  useEffect(() => {
    // Wait until the first-sign-in consent screen is accepted so the welcome isn't hidden behind it.
    if (!isNew || !user?.consent_accepted) return;
    toast.success("Your email is verified. Add your details below and your CV will build itself.", { id: "profile-new" });
    window.history.replaceState(null, "", "/dashboard/profile");
  }, [isNew, user?.consent_accepted]);

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const body = () => pick(p);
  const score = useMemo(() => (p ? Math.round((CHECKS.filter((c) => c(p)).length / CHECKS.length) * 100) : 0), [p]);

  useEffect(() => {
    // Autosave shortly after typing stops; each save lets the CV rewrite itself.
    if (!p) return;
    const snap = JSON.stringify(pick(p));
    if (snap === saved) return;
    const t = setTimeout(async () => {
      setStatus("saving");
      try {
        await api.put("/profile", JSON.parse(snap));
        if (JSON.parse(snap).name !== JSON.parse(saved || "{}").name) checkAuth();
        setSaved(snap);
        setStatus("saved");
        setVersion((v) => v + 1);
      } catch (e) {
        setStatus("error");
        toast.error(errText(e, "We couldn't save your changes. Please check your connection."), { id: "profile-save" });
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [p, saved]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!p) return <div className="flex items-center justify-center py-32"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>;
  const dirty = JSON.stringify(body()) !== saved;
  const shown = dirty && status !== "error" ? "saving" : status;
  const block = (key, node) => (
    <div onFocusCapture={() => setFocus(key)} data-testid={`profile-block-${key}`}>
      {node}
      {focus === key && <div className="xl:hidden mt-3"><KeshTip help={PROFILE_HELP[key]} testid={`profile-${key}`} /></div>}
    </div>
  );
  return (
    <div className="af-fade-up space-y-6 pb-4" data-testid="profile-view">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">My profile</h1>
          <span data-testid="profile-save-status" data-status={shown} className={`inline-flex items-center gap-1.5 text-sm ${shown === "error" ? "text-danger" : "text-body"}`}>
            {shown === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : shown === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-success" />}
            {shown === "saving" ? "Saving…" : shown === "error" ? "Not saved yet" : "All changes saved"}
          </span>
        </div>
        <p className="text-body mt-1">Your details in one place. Everything saves as you type, and your CV rewrites itself.</p>
      </div>
      <ProfileHeader p={p} score={score} onChange={set} onVerified={() => { set("email_verified", true); checkAuth(); }} />
      <div className="grid xl:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <StatusTips status={p.work_status} onGo={onGo} />
          {block("about", <AboutSection p={p} onChange={set} />)}
          {block("experience", <ProfileList kind="experience" items={p.experience} onChange={(v) => set("experience", v)} />)}
          {block("education", <ProfileList kind="education" items={p.education} onChange={(v) => set("education", v)} />)}
          {block("skills", <SkillsSection p={p} onChange={set} />)}
          {block("cv", <ProfileCv body={body} baseCv={baseCv} version={version} onGo={onGo} onBuilt={onCvChange} />)}
          <ProfileSettings p={p} onChange={set} />
          <InviteFriends />
        </div>
        <aside className="hidden xl:block sticky top-24" data-testid="profile-kesh-rail">
          <KeshTip help={PROFILE_HELP[focus || "start"]} testid="profile-rail" />
        </aside>
      </div>
    </div>
  );
}
